import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {
  api,
  type Foundry,
  type FoundryPart,
  type StructureDetail,
  type StructureWrite,
} from '../lib/api';

/**
 * "Configure From Casting Number" page. Mirrors the legacy
 * `resources/views/foundry/lookup.blade.php` ("Search by Casting"): pick a
 * foundry, then browse that foundry's castings in a filterable table. Only
 * parts that have a valid FlexStorm style number for the chosen
 * permanent/oil combination are shown (legacy `StyleNumbe($permanent,$oil)`).
 * Selecting a part creates the structure directly from the foundry part and
 * lands on the structure report — no drainage-family / dimensions step.
 */

/** Legacy `foundry_part->StyleNumbe($permanent, $oil)`. */
function styleNumberFor(part: FoundryPart, permanent: boolean, oil: boolean): string {
  const raw = permanent
    ? oil
      ? part.partNumberPermanentOilFilter
      : part.partNumberPermanentNoFilter
    : part.partNumberTemporary;
  return (raw ?? '').trim();
}

/** Legacy `GetDimensionStringGrateAttribute`. */
function grateDimensionString(part: FoundryPart): string {
  if (part.grateSizeWidth != null && part.grateSizeHeight != null) {
    return `${part.grateSizeWidth} x ${part.grateSizeHeight}`;
  }
  if (part.grateSizeDiameter != null) return `${part.grateSizeDiameter}`;
  return '—';
}

/** Legacy `GetDimensionStringOpeningAttribute`. */
function openingDimensionString(part: FoundryPart): string {
  if (part.openingSizeWidth != null && part.openingSizeHeight != null) {
    return `${part.openingSizeWidth} x ${part.openingSizeHeight}`;
  }
  if (part.openingSizeDiameter != null) return `${part.openingSizeDiameter}`;
  return '—';
}

type SortKey = 'casting' | 'part' | 'lineNote' | 'inlet' | 'grate' | 'opening';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'casting', label: 'Casting Number' },
  { key: 'part', label: 'Part Number' },
  { key: 'lineNote', label: 'Line Note/Size' },
  { key: 'inlet', label: 'Inlet Type' },
  { key: 'grate', label: 'Grate Size' },
  { key: 'opening', label: 'Opening Size' },
];

function columnValue(part: FoundryPart, key: SortKey, permanent: boolean, oil: boolean): string {
  switch (key) {
    case 'casting':
      return part.foundryPartNumber ?? '';
    case 'part':
      return styleNumberFor(part, permanent, oil);
    case 'lineNote':
      return part.lineNote ?? '';
    case 'inlet':
      return part.inletType ?? '';
    case 'grate':
      return grateDimensionString(part);
    case 'opening':
      return openingDimensionString(part);
  }
}

export function CastingLookupPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isPermanent = params.get('temp') !== 'true';
  const oilFiltration = isPermanent && params.get('oil') === 'true';
  const editId = params.get('id')?.trim() || null;
  const initialFoundryId = Number(params.get('foundryId')) || null;
  const initialCasting = params.get('casting') ?? '';

  const [foundryId, setFoundryId] = useState<number | null>(initialFoundryId);
  const [filter, setFilter] = useState(initialCasting);
  const [creatingForId, setCreatingForId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dimensionsHref = `/newstructure/select?temp=${isPermanent ? 'false' : 'true'}&oil=${oilFiltration ? 'true' : 'false'}&mode=dimensions${editId ? `&id=${editId}` : ''}`;

  const foundriesQuery = useQuery({
    queryKey: ['foundries'],
    queryFn: () => api.get<Foundry[]>('/api/v1/foundries'),
  });

  // Legacy `foundry::with('foundry_parts')->get()` had no orderBy, so the
  // dropdown followed database/insertion (id) order. The shared endpoint sorts
  // alphabetically by name, so re-sort by id here to match the old tool.
  const foundries = useMemo(
    () => [...(foundriesQuery.data ?? [])].sort((a, b) => a.id - b.id),
    [foundriesQuery.data],
  );

  const partsQuery = useQuery({
    queryKey: ['foundry-parts', foundryId],
    queryFn: () => api.get<FoundryPart[]>(`/api/v1/foundries/${foundryId}/parts`),
    enabled: foundryId != null,
  });

  // When editing an existing structure we need its current draft state so the
  // PUT doesn't silently flip it (legacy edits never change isdraft).
  const existingQuery = useQuery({
    queryKey: ['structure', editId],
    queryFn: () => api.get<StructureDetail>(`/api/v1/structures/${editId}`),
    enabled: editId != null,
  });

  const [sortKey, setSortKey] = useState<SortKey>('casting');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  // Only castings valid for this permanent/oil combination, then apply the
  // free-text casting-number filter (legacy DataTable "Filter" box).
  const parts = useMemo(() => {
    const all = partsQuery.data ?? [];
    const needle = filter.trim().toLowerCase();
    return all
      .filter((p) => styleNumberFor(p, isPermanent, oilFiltration) !== '')
      .filter((p) =>
        needle.length === 0
          ? true
          : p.foundryPartNumber.toLowerCase().includes(needle),
      );
  }, [partsQuery.data, filter, isPermanent, oilFiltration]);

  // Column sorting (legacy DataTables: all data columns sortable, numeric-aware).
  const sorted = useMemo(() => {
    const arr = [...parts];
    arr.sort((a, b) => {
      const cmp = columnValue(a, sortKey, isPermanent, oilFiltration).localeCompare(
        columnValue(b, sortKey, isPermanent, oilFiltration),
        undefined,
        { numeric: true, sensitivity: 'base' },
      );
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [parts, sortKey, sortDir, isPermanent, oilFiltration]);

  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const startIndex = currentPage * pageSize;
  const visibleParts = sorted.slice(startIndex, startIndex + pageSize);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const choose = async (part: FoundryPart) => {
    setCreatingForId(part.id);
    setError(null);
    try {
      const body: StructureWrite = {
        nickname: null,
        permanent: isPermanent,
        oilFiltration,
        structureTypeId: null,
        grateStyleId: null,
        foundryId: part.foundryId,
        foundryPartId: part.id,
        extendedBottomHeavyDuty: false,
        // Legacy parity: new structures are drafts (isdraft defaults true);
        // editing preserves the existing draft state.
        isDraft: editId ? (existingQuery.data?.isDraft ?? true) : true,
      };
      const saved = editId
        ? await api.put<StructureDetail>(`/api/v1/structures/${editId}`, body)
        : await api.post<StructureDetail>('/api/v1/structures', body);
      // The report/notes screens read the structure via React Query with a 30s
      // staleTime; without invalidating, a just-changed casting would be masked
      // by the stale cache (visible as "didn't save until the second edit").
      await queryClient.invalidateQueries({ queryKey: ['structure'] });
      navigate(`/newstructure/notes?id=${saved.hashId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create structure');
      setCreatingForId(null);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row mt-lg-3 mt-1">
        <div className="col col-xl-12 col-lg-12 text-center">
          <h1>Search by Casting</h1>
          <p>
            Select a foundry below to view common castings or use the filter field to
            search for a specific casting number. Once you select a part, you will be
            able to add additional comments or photos. If you don&rsquo;t see the foundry
            or casting number you need listed, you can configure your structure
            using&nbsp;dimensions.
          </p>
        </div>
      </div>

      <div className="row mt-lg-3 mt-1 justify-content-center border-bottom">
        <div className="col-12 col-xl-6 col-lg-6 mb-1 pb-1">
          <select
            id="foundry"
            className="form-select form-select-lg"
            value={foundryId ?? ''}
            onChange={(e) => {
              setFoundryId(e.target.value ? Number(e.target.value) : null);
              setFilter('');
              setError(null);
            }}
          >
            <option value="" hidden>
              Select A Foundry*
            </option>
            {foundries.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12 col-xl-1 col-lg-1 text-center mb-1 pb-1">
          <OverlayTrigger
            placement="right"
            overlay={<Tooltip id="tt-foundry">Choose the foundry your frame and grate are produced by&nbsp;below.</Tooltip>}
          >
            <img
              src="/images/ICON/Icon_Info.png"
              alt=""
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </OverlayTrigger>
        </div>
      </div>

      {error && (
        <div className="row mt-2 justify-content-center">
          <div className="col-12 col-xl-6 col-lg-6">
            <div className="alert alert-warning mb-0" role="alert">
              {error}
            </div>
          </div>
        </div>
      )}

      {foundryId != null && (
        <div className="row mt-lg-1 pt-3 mt-1">
          <div className="col-12">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
              <label className="d-flex align-items-center mb-0">
                <span className="me-2">Show</span>
                <select
                  className="form-select form-select-sm w-auto"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(0);
                  }}
                  disabled={partsQuery.isLoading}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span className="ms-2">entries</span>
              </label>
              <input
                type="text"
                className="form-control w-auto"
                placeholder="Filter by casting number"
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setPage(0);
                }}
                disabled={partsQuery.isLoading}
              />
            </div>

            {partsQuery.isLoading ? (
              <p className="text-muted text-center">Loading castings…</p>
            ) : parts.length === 0 ? (
              <p className="text-muted text-center">
                No matching castings for this foundry. Try a different foundry or{' '}
                <Link to={dimensionsHref}>configure with dimensions</Link>.
              </p>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table w-100">
                    <thead>
                      <tr>
                        {COLUMNS.map((c) => (
                          <th
                            key={c.key}
                            role="button"
                            onClick={() => toggleSort(c.key)}
                            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                          >
                            {c.label}
                            <span className="ms-1 text-muted">
                              {sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                            </span>
                          </th>
                        ))}
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {visibleParts.map((p) => (
                        <tr key={p.id}>
                          <td style={{ verticalAlign: 'middle' }}>{p.foundryPartNumber}</td>
                          <td style={{ verticalAlign: 'middle' }}>
                            {styleNumberFor(p, isPermanent, oilFiltration)}
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>{p.lineNote || '—'}</td>
                          <td style={{ verticalAlign: 'middle' }}>{p.inletType || '—'}</td>
                          <td style={{ verticalAlign: 'middle' }}>{grateDimensionString(p)}</td>
                          <td style={{ verticalAlign: 'middle' }}>{openingDimensionString(p)}</td>
                          <td style={{ verticalAlign: 'middle', textAlign: 'end' }}>
                            <button
                              type="button"
                              onClick={() => choose(p)}
                              disabled={creatingForId !== null}
                              className="btn btn-sm btn-outline-primary"
                            >
                              {creatingForId === p.id ? 'Creating…' : 'Select Structure'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex flex-wrap justify-content-between align-items-center mt-2 gap-2">
                  <div className="text-muted small">
                    Showing {startIndex + 1} to {Math.min(startIndex + pageSize, total)} of{' '}
                    {total} entries
                  </div>
                  <ul className="pagination pagination-rounded mb-0">
                    <li className={`page-item ${currentPage === 0 ? 'disabled' : ''}`}>
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => setPage(currentPage - 1)}
                        disabled={currentPage === 0}
                      >
                        Previous
                      </button>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link">
                        {currentPage + 1} / {pageCount}
                      </span>
                    </li>
                    <li className={`page-item ${currentPage >= pageCount - 1 ? 'disabled' : ''}`}>
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => setPage(currentPage + 1)}
                        disabled={currentPage >= pageCount - 1}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="row">
        <div className="col align-self-center text-center">
          <Link to={dimensionsHref} className="btn btn-sm btn-secondary mt-2">
            Cancel And Configure With&nbsp;Dimensions
          </Link>
        </div>
      </div>
    </div>
  );
}
